/* eslint-disable max-lines */
/**
 * Diff Preview Dialog
 * Shows side-by-side comparison before applying text changes
 */

import { useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { ArrowLeft, Check, Columns2, FileText, Maximize2, Minimize2, X } from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { calculateContextDiff, extractSectionName, identifyChangedLines } from "./diff-utils"

type DiffPreviewDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  original: string
  modified: string
  onAccept: () => void
  onReject: () => void
  operationName: string
}

type ViewMode = "side-by-side" | "inline-highlights"

type DiffChunk = {
  type: "added" | "removed" | "unchanged"
  text: string
}

type LineByLineRendererProps = {
  content: string
  changedLines: Set<number>
  changeType: "added" | "removed"
}

/**
 * Renders markdown content line-by-line with highlighting for changed lines only
 */
function LineByLineRenderer({ content, changedLines, changeType }: LineByLineRendererProps) {
  const lines = content.split("\n")

  return (
    <div className="space-y-0">
      {lines.map((line, index) => {
        const isChanged = changedLines.has(index)

        let bgColor = ""
        if (isChanged) {
          bgColor = changeType === "added" ? "bg-success/15" : "bg-destructive/15"
        }

        // Add visual emphasis for changed lines
        let borderClasses = ""
        if (isChanged) {
          borderClasses =
            changeType === "added"
              ? "border-l-2 border-success shadow-sm"
              : "border-l-2 border-destructive shadow-sm"
        }

        return (
          <div
            key={index}
            className={cn(
              "min-h-[1.5rem] px-2 py-0.5 transition-[background-color,box-shadow]",
              bgColor,
              borderClasses,
              isChanged && "ring-1 ring-inset",
              isChanged && changeType === "added"
                ? "ring-success/20"
                : isChanged && "ring-destructive/20"
            )}
          >
            <MarkdownRenderer content={line || " "} />
          </div>
        )
      })}
    </div>
  )
}

// Simple word-level diff algorithm
function computeWordDiff(original: string, modified: string): DiffChunk[] {
  const originalWords = original.split(/(\s+)/)
  const modifiedWords = modified.split(/(\s+)/)
  const chunks: DiffChunk[] = []
  const lookAhead = 3

  // Helper to find word in slice
  const findMatch = (word: string, words: string[], start: number): number => {
    const end = Math.min(start + lookAhead, words.length)
    for (let k = start; k < end; k++) {
      if (words[k] === word) return k
    }
    return -1
  }

  let i = 0
  let j = 0

  while (i < originalWords.length || j < modifiedWords.length) {
    // Handle remaining additions
    if (i >= originalWords.length) {
      chunks.push({ type: "added", text: modifiedWords[j] })
      j++
      continue
    }

    // Handle remaining removals
    if (j >= modifiedWords.length) {
      chunks.push({ type: "removed", text: originalWords[i] })
      i++
      continue
    }

    // Words match - keep as unchanged
    if (originalWords[i] === modifiedWords[j]) {
      chunks.push({ type: "unchanged", text: originalWords[i] })
      i++
      j++
      continue
    }

    // Words differ - look ahead for matches
    const matchInModified = findMatch(originalWords[i], modifiedWords, j + 1)
    const matchInOriginal = findMatch(modifiedWords[j], originalWords, i + 1)

    // Original word found soon in modified - mark intermediate as additions
    if (matchInModified !== -1) {
      for (let l = j; l < matchInModified; l++) {
        chunks.push({ type: "added", text: modifiedWords[l] })
      }
      j = matchInModified
      continue
    }

    // Modified word found soon in original - mark intermediate as removals
    if (matchInOriginal !== -1) {
      for (let l = i; l < matchInOriginal; l++) {
        chunks.push({ type: "removed", text: originalWords[l] })
      }
      i = matchInOriginal
      continue
    }

    // No match found - mark both as changed
    chunks.push({ type: "removed", text: originalWords[i] })
    chunks.push({ type: "added", text: modifiedWords[j] })
    i++
    j++
  }

  return chunks
}

export function DiffPreviewDialog({
  open,
  onOpenChange,
  original,
  modified,
  onAccept,
  onReject,
  operationName,
}: DiffPreviewDialogProps) {
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<ViewMode>("side-by-side")
  const [showFullDiff, setShowFullDiff] = useState(false)
  const originalScrollRef = useRef<HTMLDivElement>(null)
  const modifiedScrollRef = useRef<HTMLDivElement>(null)
  const isSyncingScroll = useRef(false)

  // Calculate context-only diff for better visibility
  const contextDiff = useMemo(
    () => calculateContextDiff(original, modified, 3),
    [original, modified]
  )

  // Extract section name from the changes for header display
  const sectionName = useMemo(() => {
    // Try to extract from the original selection (before transformation)
    const changedLines = contextDiff.originalContext.split("\n").filter((line) => line.trim())
    return extractSectionName(changedLines.join("\n"))
  }, [contextDiff.originalContext])

  // Use context diff for display, or full content if toggle is enabled
  const displayOriginal = showFullDiff ? original : contextDiff.originalContext
  const displayModified = showFullDiff ? modified : contextDiff.modifiedContext

  // Identify which lines are changed for precise highlighting
  const changedLines = useMemo(
    () => identifyChangedLines(displayOriginal, displayModified),
    [displayOriginal, displayModified]
  )

  const diffChunks = useMemo(
    () => computeWordDiff(displayOriginal, displayModified),
    [displayOriginal, displayModified]
  )

  // Synchronized scrolling for side-by-side view
  useEffect(() => {
    const originalEl = originalScrollRef.current
    const modifiedEl = modifiedScrollRef.current

    if (!originalEl || !modifiedEl || viewMode !== "side-by-side") {
      return
    }

    const handleOriginalScroll = () => {
      if (isSyncingScroll.current) return
      isSyncingScroll.current = true
      modifiedEl.scrollTop = originalEl.scrollTop
      requestAnimationFrame(() => {
        isSyncingScroll.current = false
      })
    }

    const handleModifiedScroll = () => {
      if (isSyncingScroll.current) return
      isSyncingScroll.current = true
      originalEl.scrollTop = modifiedEl.scrollTop
      requestAnimationFrame(() => {
        isSyncingScroll.current = false
      })
    }

    originalEl.addEventListener("scroll", handleOriginalScroll)
    modifiedEl.addEventListener("scroll", handleModifiedScroll)

    return () => {
      originalEl.removeEventListener("scroll", handleOriginalScroll)
      modifiedEl.removeEventListener("scroll", handleModifiedScroll)
    }
  }, [viewMode, showFullDiff, displayOriginal, displayModified])

  const handleAccept = () => {
    onAccept()
    onOpenChange(false)
  }

  const handleReject = () => {
    onReject()
    onOpenChange(false)
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[95vh] flex-col p-0 sm:h-[90vh] sm:max-w-[95vw] [&>button]:hidden">
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-3 border-b px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">
                {t("canvas.preview_changes", "Preview Changes")}
              </h2>
              {!showFullDiff && contextDiff.totalLines > 5 && (
                <span
                  className={cn(
                    "ring-foreground/10 inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset",
                    STATUS_TONES.info.solid
                  )}
                >
                  📝 {sectionName}
                </span>
              )}
            </div>
            <div className="mt-1 space-y-0.5">
              <p className="text-muted-foreground text-sm">{operationName}</p>
              {!showFullDiff && contextDiff.totalLines > 5 && (
                <p className="text-muted-foreground text-xs">
                  Showing lines {contextDiff.startLine}-{contextDiff.endLine} of{" "}
                  {contextDiff.totalLines} total •{" "}
                  {contextDiff.hasLinesBefore && `${contextDiff.linesBefore} lines above • `}
                  Changes {contextDiff.hasLinesAfter && `• ${contextDiff.linesAfter} lines below`}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* View Mode Toggle */}
            <div className="bg-muted/30 flex items-center gap-1 rounded-lg border p-1">
              <Button
                size="sm"
                variant="ghost"
                className={cn(
                  "text-xs sm:text-sm",
                  viewMode === "side-by-side" && "bg-background hover:bg-background shadow-sm"
                )}
                onClick={() => setViewMode("side-by-side")}
              >
                <Columns2 className="mr-1 h-3 w-3 sm:mr-2 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">
                  {t("canvas.diff_preview.side_by_side", "Side by Side")}
                </span>
                <span className="sm:hidden">{t("canvas.diff_preview.split", "Split")}</span>
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className={cn(
                  "text-xs sm:text-sm",
                  viewMode === "inline-highlights" && "bg-background hover:bg-background shadow-sm"
                )}
                onClick={() => setViewMode("inline-highlights")}
              >
                <FileText className="mr-1 h-3 w-3 sm:mr-2 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">
                  {t("canvas.diff_preview.highlights", "Highlights")}
                </span>
                <span className="sm:hidden">{t("canvas.diff_preview.inline", "Inline")}</span>
              </Button>
            </div>

            {/* Show Full Diff Toggle */}
            {contextDiff.totalLines > 5 && (
              <Button
                className="hidden sm:flex"
                size="sm"
                variant="outline"
                onClick={() => setShowFullDiff(!showFullDiff)}
              >
                {showFullDiff ? (
                  <>
                    <Minimize2 className="mr-2 h-4 w-4" />
                    {t("canvas.diff_preview.show_context", "Show Context")}
                  </>
                ) : (
                  <>
                    <Maximize2 className="mr-2 h-4 w-4" />
                    {t("canvas.diff_preview.show_full", "Show Full Diff")}
                  </>
                )}
              </Button>
            )}

            {/* Actions - separated with border */}
            <div className="flex items-center gap-1.5 border-l pl-2 sm:gap-2 sm:pl-3">
              <Button className="hidden sm:flex" size="sm" variant="ghost" onClick={handleClose}>
                {t("canvas.diff_preview.close", "Close")}
              </Button>
              <Button
                aria-label={t("canvas.diff_preview.close")}
                className="sm:hidden"
                size="icon"
                variant="ghost"
                onClick={handleClose}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Button className="hidden sm:flex" size="sm" variant="outline" onClick={handleReject}>
                <X className="mr-2 h-4 w-4" />
                {t("canvas.diff_preview.reject", "Reject")}
              </Button>
              <Button
                aria-label={t("canvas.diff_preview.reject")}
                className="sm:hidden"
                size="icon"
                variant="outline"
                onClick={handleReject}
              >
                <X className="h-4 w-4" />
              </Button>
              <Button className="hidden sm:flex" size="sm" onClick={handleAccept}>
                <Check className="mr-2 h-4 w-4" />
                {t("canvas.diff_preview.accept", "Accept")}
              </Button>
              <Button
                aria-label={t("canvas.diff_preview.accept")}
                className="sm:hidden"
                size="icon"
                onClick={handleAccept}
              >
                <Check className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        {viewMode === "side-by-side" ? (
          /* Side by Side Comparison */
          <div className="grid min-h-0 flex-1 grid-cols-2 divide-x overflow-hidden">
            {/* Original */}
            <div className="flex min-h-0 flex-col overflow-hidden">
              <div className="bg-muted/30 shrink-0 border-b px-4 py-2">
                <h3 className="text-sm font-semibold">
                  {t("canvas.diff_preview.original", "Original")}
                </h3>
              </div>
              <div ref={originalScrollRef} className="h-full overflow-y-auto">
                {/* Lines above indicator */}
                {!showFullDiff && contextDiff.hasLinesBefore && (
                  <div className="bg-muted/20 text-muted-foreground flex items-center justify-center border-b py-3 text-xs">
                    <span>⋮ {contextDiff.linesBefore} lines above</span>
                  </div>
                )}
                <div className="p-2">
                  <LineByLineRenderer
                    changedLines={changedLines.changedOriginalLines}
                    changeType="removed"
                    content={displayOriginal}
                  />
                </div>
                {/* Lines below indicator */}
                {!showFullDiff && contextDiff.hasLinesAfter && (
                  <div className="bg-muted/20 text-muted-foreground flex items-center justify-center border-t py-3 text-xs">
                    <span>⋮ {contextDiff.linesAfter} lines below</span>
                  </div>
                )}
              </div>
            </div>

            {/* Modified */}
            <div className="flex min-h-0 flex-col overflow-hidden">
              <div className="bg-success/15 shrink-0 border-b px-4 py-2">
                <h3 className="text-success text-sm font-semibold">
                  {t("canvas.diff_preview.modified", "Modified")}
                </h3>
              </div>
              <div ref={modifiedScrollRef} className="h-full overflow-y-auto">
                {/* Lines above indicator */}
                {!showFullDiff && contextDiff.hasLinesBefore && (
                  <div className="bg-muted/20 text-muted-foreground flex items-center justify-center border-b py-3 text-xs">
                    <span>⋮ {contextDiff.linesBefore} lines above</span>
                  </div>
                )}
                <div className="p-2">
                  <LineByLineRenderer
                    changedLines={changedLines.changedModifiedLines}
                    changeType="added"
                    content={displayModified}
                  />
                </div>
                {/* Lines below indicator */}
                {!showFullDiff && contextDiff.hasLinesAfter && (
                  <div className="bg-muted/20 text-muted-foreground flex items-center justify-center border-t py-3 text-xs">
                    <span>⋮ {contextDiff.linesAfter} lines below</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Inline Highlights View */
          <div className="min-h-0 flex-1 overflow-hidden">
            <div className="flex h-full flex-col overflow-hidden">
              <div className="bg-muted/30 shrink-0 border-b px-4 py-2">
                <h3 className="text-sm font-semibold">
                  {t("canvas.diff_preview.changes_highlighted", "Changes Highlighted")}
                </h3>
                <p className="text-muted-foreground text-xs">
                  <span
                    className={cn(
                      "inline-block rounded px-1.5 py-0.5 font-medium",
                      STATUS_TONES.success.solid
                    )}
                  >
                    {t("canvas.diff_preview.added", "Added")}
                  </span>{" "}
                  •{" "}
                  <span
                    className={cn(
                      "inline-block rounded px-1.5 py-0.5 font-medium line-through",
                      STATUS_TONES.destructive.solid
                    )}
                  >
                    {t("canvas.diff_preview.removed", "Removed")}
                  </span>
                </p>
              </div>
              <ScrollArea className="h-full">
                <div className="p-4">
                  <div className="font-mono text-sm leading-relaxed wrap-break-word whitespace-pre-wrap">
                    {diffChunks.map((chunk, idx) => {
                      if (chunk.type === "unchanged") {
                        return <span key={idx}>{chunk.text}</span>
                      } else if (chunk.type === "added") {
                        return (
                          <span
                            key={idx}
                            className={cn(
                              "bg-success/15 text-foreground rounded px-1 py-0.5 font-semibold"
                            )}
                          >
                            {chunk.text}
                          </span>
                        )
                      } else {
                        return (
                          <span
                            key={idx}
                            className={cn(
                              "bg-destructive/15 text-destructive decoration-destructive rounded px-1 py-0.5 font-semibold line-through decoration-2"
                            )}
                          >
                            {chunk.text}
                          </span>
                        )
                      }
                    })}
                  </div>
                </div>
              </ScrollArea>
            </div>
          </div>
        )}

        {/* Footer with stats */}
        <div className="shrink-0 border-t px-6 py-3">
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span>
              {t("canvas.diff_preview.original", "Original")}: {displayOriginal.length}{" "}
              {t("canvas.diff_preview.characters", "characters")} •{" "}
              {t("canvas.diff_preview.modified", "Modified")}: {displayModified.length}{" "}
              {t("canvas.diff_preview.characters", "characters")}
            </span>
            <span>
              {t("canvas.diff_preview.change", "Change")}:{" "}
              {displayModified.length > displayOriginal.length ? "+" : ""}
              {displayModified.length - displayOriginal.length}{" "}
              {t("canvas.diff_preview.characters", "characters")}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
